import React, { forwardRef } from 'react';
import { CONFIG } from '../data/config';
import { CartItemType, CheckoutData } from '../types/types';
interface OrderTicketProps {
  orderNumber: string;
  data: CheckoutData;
  items: CartItemType[];
  cartTotal: number;
  totalFinal: number;
}
export const OrderTicket = forwardRef<HTMLDivElement, OrderTicketProps>(({
  orderNumber,
  data,
  items,
  cartTotal,
  totalFinal
}, ref) => {
  const expressFee = data.entrega === 'Express' ? CONFIG.EXPRESS_FEE : 0;
  const vuelto = data.metodoPago === 'Efectivo' && data.montoEfectivo ? parseInt(data.montoEfectivo) - totalFinal : 0;
  return <div ref={ref} className="bg-surface w-[340px] p-6 font-mono text-sm border border-gray-800 shadow-2xl relative overflow-hidden" data-id="element-274">
        {/* Ticket zig-zag top edge (simulated with CSS) */}
        <div className="absolute top-0 left-0 right-0 h-2 bg-repeat-x bg-[length:10px_10px] bg-[radial-gradient(circle_at_50%_0,transparent_5px,#1A1A1A_6px)]" data-id="element-275" />

        {/* Header */}
        <div className="text-center mb-6 pt-2" data-id="element-276">
          <div className="w-16 h-16 mx-auto mb-3 grayscale contrast-200" data-id="element-277">
            <img src={CONFIG.LOGO_URL} alt={CONFIG.APP_NAME} className="w-full h-full object-contain rounded-full border border-gray-700" data-id="element-278" />
          </div>
          <h1 className="font-bold text-xl text-white tracking-widest uppercase" data-id="element-279">
            {CONFIG.APP_NAME}
          </h1>
          <p className="text-xs text-gray-400 mt-1 uppercase tracking-wider" data-id="element-280">
            {CONFIG.APP_TAGLINE}
          </p>
          <div className="border-b-2 border-dashed border-gray-700 my-4" data-id="element-281" />
          <p className="font-bold text-lg text-white" data-id="element-282">PEDIDO #{orderNumber}</p>
          <p className="text-xs text-gray-500 mt-1" data-id="element-283">
            {new Date().toLocaleDateString('es-CR', {
          weekday: 'short',
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })}
          </p>
        </div>

        {/* Customer Info */}
        <div className="mb-6 space-y-1 text-gray-300" data-id="element-284">
          <p data-id="element-285">
            <span className="font-semibold text-white" data-id="element-286">Cliente:</span>{' '}
            {data.nombre}
          </p>
          <p data-id="element-287">
            <span className="font-semibold text-white" data-id="element-288">Tel:</span>{' '}
            {data.telefono}
          </p>
          <p data-id="element-289">
            <span className="font-semibold text-white" data-id="element-290">Entrega:</span>{' '}
            {data.entrega}
          </p>
          <p data-id="element-291">
            <span className="font-semibold text-white" data-id="element-292">Pago:</span>{' '}
            {data.metodoPago}
          </p>
        </div>

        <div className="border-b-2 border-dashed border-gray-700 mb-4" data-id="element-293" />

        {/* Items */}
        <div className="mb-6 space-y-4" data-id="element-294">
          <div className="flex justify-between font-bold text-white mb-2 pb-2 border-b border-gray-800" data-id="element-295">
            <span data-id="element-296">CANT DESCRIPCIÓN</span>
            <span data-id="element-297">TOTAL</span>
          </div>

          {items.map((item, idx) => <div key={idx} className="text-gray-300" data-id="element-298">
              <div className="flex justify-between font-bold text-white" data-id="element-299">
                <span data-id="element-300">
                  {item.quantity}x {item.name}
                </span>
                <span data-id="element-301">
                  {CONFIG.CURRENCY_SYMBOL}
                  {(item.subtotal * item.quantity).toLocaleString()}
                </span>
              </div>

              <div className="pl-4 text-xs mt-1 space-y-0.5 text-gray-400" data-id="element-302">
                {item.selections.base && <p data-id="element-303">Base: {item.selections.base.name}</p>}
                {item.selections.proteina && <p data-id="element-304">Prot: {item.selections.proteina.name}</p>}
                {item.selections.toppings && item.selections.toppings.length > 0 && <p data-id="element-305">
                      Toppings:{' '}
                      {item.selections.toppings.map(t => t.name).join(', ')}
                    </p>}
                {item.selections.toppingsPremium && item.selections.toppingsPremium.length > 0 && <p data-id="element-306">
                      Premium:{' '}
                      {item.selections.toppingsPremium.map(t => t.name).join(', ')}
                    </p>}
                {item.selections.aderezos && item.selections.aderezos.length > 0 && <p data-id="element-307">
                      Aderezo:{' '}
                      {item.selections.aderezos.map(t => t.name).join(', ')}
                    </p>}
                {item.selections.semillas && item.selections.semillas.length > 0 && <p data-id="element-308">
                      Semillas:{' '}
                      {item.selections.semillas.map(t => t.name).join(', ')}
                    </p>}
                {item.selections.comentarios && <p className="italic mt-1" data-id="element-309">
                    Nota: {item.selections.comentarios}
                  </p>}
              </div>
            </div>)}
        </div>

        <div className="border-b-2 border-dashed border-gray-700 mb-4" data-id="element-310" />

        {/* Totals */}
        <div className="space-y-1 mb-6 text-gray-300" data-id="element-311">
          <div className="flex justify-between" data-id="element-312">
            <span data-id="element-313">Subtotal</span>
            <span data-id="element-314">
              {CONFIG.CURRENCY_SYMBOL}
              {cartTotal.toLocaleString()}
            </span>
          </div>
          {data.entrega === 'Express' && <div className="flex justify-between" data-id="element-315">
              <span data-id="element-316">Express</span>
              <span data-id="element-317">
                {CONFIG.CURRENCY_SYMBOL}
                {CONFIG.EXPRESS_FEE.toLocaleString()}
              </span>
            </div>}
          <div className="flex justify-between font-bold text-lg pt-2 mt-2 border-t border-gray-800 text-white" data-id="element-318">
            <span data-id="element-319">TOTAL</span>
            <span data-id="element-320">
              {CONFIG.CURRENCY_SYMBOL}
              {totalFinal.toLocaleString()}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-gray-500 mt-8" data-id="element-321">
          <p className="font-bold text-gray-400 mb-1" data-id="element-322">
            ¡GRACIAS POR TU COMPRA!
          </p>
          <p data-id="element-323">Síguenos en redes sociales</p>
          <p data-id="element-324">@{CONFIG.APP_NAME.replace(/\s+/g, '').toLowerCase()}</p>
        </div>

        {/* Ticket zig-zag bottom edge */}
        <div className="absolute bottom-0 left-0 right-0 h-2 bg-repeat-x bg-[length:10px_10px] bg-[radial-gradient(circle_at_50%_10px,transparent_5px,#1A1A1A_6px)]" data-id="element-325" />
      </div>;
});
function renderSelections(item: CartItemType) {
  const s = item.selections;
  const lines: string[] = [];
  if (s.base) lines.push(`Base: ${s.base.name}`);
  if (s.proteina) lines.push(`Proteína: ${s.proteina.name}`);
  if (s.toppings?.length) lines.push(`Toppings: ${s.toppings.map(t => t.name).join(', ')}`);
  if (s.toppingsPremium?.length) lines.push(`Premium: ${s.toppingsPremium.map(t => t.name).join(', ')}`);
  if (s.aderezos?.length) lines.push(`Aderezos: ${s.aderezos.map(t => t.name).join(', ')}`);
  if (s.semillas?.length) lines.push(`Semillas: ${s.semillas.map(t => t.name).join(', ')}`);
  if (s.comentarios) lines.push(`Notas: ${s.comentarios}`);
  if (lines.length === 0) return null;
  return <div style={{
    paddingLeft: 16
  }} data-id="element-326">
      {lines.map((line, i) => <div key={i} style={{
      fontSize: 11,
      color: '#6b7280',
      lineHeight: 1.5
    }} data-id="element-327">
          • {line}
        </div>)}
    </div>;
}
const sectionTitleStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  color: '#8CB33F',
  letterSpacing: 1.5,
  textTransform: 'uppercase',
  marginBottom: 8,
  fontFamily: "'Poppins', sans-serif"
};
const rowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 4
};
const labelStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#6b7280'
};
const valueStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: '#1f2937'
};